"use client";

import clsx from "clsx";
import {useCallback, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import GoogleAutoComplete, {
   Address as AutoCompleteSelection,
} from "@/components/common/GoogleAutoComplete";
import PhoneInput from "@/components/common/PhoneInput";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import Input from "@/components/library/Input";
import {ProductFocus} from "@/constants/onboarding";
import {TwilioBusinessType} from "@/enums/twilio";
import {useOrg} from "@/hooks/useOrg";
import {apiFetch} from "@/lib/api";
import {Address} from "@/types/org";
import {areAddressesEqual, mapSelectedAddress, sanitizeAddress} from "@/utils/common/address";
import {URL_REG_EXP} from "@/utils/common/general";
import {
   BusinessIdType,
   formatBusinessId,
   getBusinessIdHelperKey,
} from "@/utils/verification/businessId";
import {CompanyInfoTwilioFields} from "./CompanyInfoTwilioFields";
import {OnboardingStepHeader} from "./OnboardingStepHeader";

interface CompanyInfoStepProps {
   readonly onBack?: () => void;
   readonly onNext: () => void;
   readonly productFocus?: ProductFocus[];
}

type CompanyInfoFormValues = {
   companyName: string;
   companyUrl: string;
   address?: Address;
   phoneNumber: string;
   businessIdType: BusinessIdType;
   businessId: string;
   twilioBusinessType: TwilioBusinessType | "";
};

export function CompanyInfoStep({onBack, onNext, productFocus}: CompanyInfoStepProps) {
   const {t} = useTranslation("onboarding");
   const {t: ts} = useTranslation("settings");
   const {org, saveOrg} = useOrg();
   const isOutbound = (productFocus ?? org?.details?.onboarding?.gailFocus ?? []).includes(
      "outbound"
   );

   // Use refs to capture initial values only once
   const initialBusinessIdType: BusinessIdType =
      org?.details?.businessIdType === "CBN" ? "CBN" : "EIN";
   const initialValuesRef = useRef<CompanyInfoFormValues>({
      companyName: org?.name || "",
      companyUrl: org?.details?.website || "",
      address: sanitizeAddress(org?.address),
      phoneNumber: org?.owner?.phoneNumber || "",
      businessIdType: initialBusinessIdType,
      businessId: org?.details?.businessId || "",
      twilioBusinessType: Object.values(TwilioBusinessType).includes(
         org?.details?.twilioBusinessType as TwilioBusinessType
      )
         ? (org?.details?.twilioBusinessType as TwilioBusinessType)
         : "",
   });

   const [values, setValues] = useState<CompanyInfoFormValues>(() => {
      const initial = initialValuesRef.current;
      return {
         ...initial,
         businessId: formatBusinessId(initial.businessIdType, initial.businessId),
      };
   });
   const [isSaving, setIsSaving] = useState(false);
   const [hasSubmitted, setHasSubmitted] = useState(false);
   const [phoneValid, setPhoneValid] = useState(true);

   const businessIdHelperKey = isOutbound
      ? getBusinessIdHelperKey(values.businessIdType, values.businessId)
      : null;

   const validation = useMemo(() => {
      const hasValidUrl = !values.companyUrl || URL_REG_EXP.test(values.companyUrl);
      const hasCompanyName = values.companyName.trim().length > 0;
      const hasCompanyUrl = values.companyUrl.trim().length > 0;
      const hasAddress = !!values.address;
      const hasTwilioFields =
         !isOutbound ||
         (!!values.businessId && !businessIdHelperKey && !!values.twilioBusinessType);

      return {
         isValid: hasCompanyName && hasAddress && hasCompanyUrl && hasValidUrl && hasTwilioFields,
         errors: {
            companyName: !hasCompanyName,
            companyUrl: !hasCompanyUrl || !hasValidUrl,
            companyUrlInvalid: hasCompanyUrl && !hasValidUrl,
            address: !hasAddress,
            businessId: isOutbound && (!values.businessId || !!businessIdHelperKey),
            twilioBusinessType: isOutbound && !values.twilioBusinessType,
         },
      };
   }, [values, isOutbound, businessIdHelperKey]);

   let businessIdHelperText: string | undefined;
   if (businessIdHelperKey) {
      businessIdHelperText = ts(businessIdHelperKey);
   } else if (hasSubmitted && validation.errors.businessId) {
      businessIdHelperText = t("validation.required");
   }

   // Check if org details have changed from initial values
   const hasOrgChanged = useCallback((): boolean => {
      const initial = initialValuesRef.current;
      if (values.companyName !== initial.companyName) return true;
      if (values.companyUrl !== initial.companyUrl) return true;
      if (values.phoneNumber !== initial.phoneNumber) return true;
      if (!areAddressesEqual(values.address, initial.address)) return true;
      if (isOutbound && values.businessIdType !== initial.businessIdType) return true;
      if (isOutbound && values.businessId !== initial.businessId) return true;
      if (isOutbound && values.twilioBusinessType !== initial.twilioBusinessType) return true;
      return false;
   }, [values, isOutbound]);

   const handleSave = useCallback(async () => {
      setHasSubmitted(true);

      if (!validation.isValid) return;
      if (values.phoneNumber.trim() && !phoneValid) return;

      const orgChanged = hasOrgChanged();

      const kickOffVerification = () => {
         if (!isOutbound) {
            return;
         }
         void apiFetch("/api/verification/prepare", "POST", {silent: true});
      };

      if (!orgChanged) {
         kickOffVerification();
         onNext();
         return;
      }

      setIsSaving(true);
      try {
         await saveOrg({
            name: values.companyName,
            address: values.address,
            owner: {
               ...org?.owner,
               phoneNumber: values.phoneNumber,
            },
            details: {
               ...org?.details,
               website: values.companyUrl,
               ...(isOutbound
                  ? {
                       businessId: formatBusinessId(values.businessIdType, values.businessId),
                       businessIdType: values.businessIdType,
                       twilioBusinessType: values.twilioBusinessType,
                    }
                  : {}),
            },
         });

         kickOffVerification();
         onNext();
      } catch (error) {
         console.error("Failed to save company info", error);
      } finally {
         setIsSaving(false);
      }
   }, [
      validation.isValid,
      hasOrgChanged,
      values,
      org?.owner,
      org?.details,
      saveOrg,
      onNext,
      isOutbound,
      phoneValid,
   ]);

   return (
      <div className="flex flex-col h-full">
         <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-xl mx-auto space-y-8">
               {/* Header */}
               <OnboardingStepHeader
                  title={t("companyInfo.title")}
                  description={t("companyInfo.subtitle")}
               />

               {/* Info card */}
               <div
                  className={clsx(
                     "rounded-xl border border-primary/30 bg-primary/5 p-4",
                     "flex items-start gap-3"
                  )}
               >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                     <Icon icon="building" className="w-5 h-5 text-primary" />
                  </div>
                  <div className="space-y-1">
                     <p className="text-sm font-medium text-foreground">
                        {t("companyInfo.infoCard.title")}
                     </p>
                     <p className="text-sm text-muted-foreground">
                        {t("companyInfo.infoCard.description")}
                     </p>
                  </div>
               </div>

               {/* Form Fields */}
               <div className="space-y-6">
                  {/* Company Name */}
                  <Input
                     label={t("companyInfo.companyName")}
                     type="text"
                     value={values.companyName}
                     onChange={(e) =>
                        setValues((prev) => ({
                           ...prev,
                           companyName: e.target.value,
                        }))
                     }
                     placeholder={t("companyInfo.companyNamePlaceholder")}
                     required
                     error={hasSubmitted && validation.errors.companyName}
                     helperText={
                        hasSubmitted && validation.errors.companyName
                           ? t("validation.required")
                           : undefined
                     }
                  />

                  {/* Company Website */}
                  <Input
                     label={t("companyInfo.companyUrl")}
                     type="url"
                     hideIcon
                     required
                     value={values.companyUrl}
                     onChange={(e) =>
                        setValues((prev) => ({
                           ...prev,
                           companyUrl: e.target.value,
                        }))
                     }
                     placeholder="mycompany.com"
                     helperText={
                        validation.errors.companyUrlInvalid
                           ? t("companyInfo.websiteHelpText")
                           : (hasSubmitted &&
                                !values.companyUrl.trim() &&
                                t("validation.required")) ||
                             undefined
                     }
                     error={hasSubmitted && validation.errors.companyUrl}
                  />

                  {/* Company Address */}
                  <GoogleAutoComplete
                     id="companyAddress"
                     name="companyAddress"
                     onAddressSelect={(address: AutoCompleteSelection) => {
                        setValues((prev) => {
                           if (!address?.isComplete) {
                              if (!prev.address) {
                                 return prev;
                              }
                              return {
                                 ...prev,
                                 address: undefined,
                              };
                           }

                           const nextAddress = mapSelectedAddress(address);
                           if (areAddressesEqual(prev.address, nextAddress)) {
                              return prev;
                           }

                           return {
                              ...prev,
                              address: nextAddress,
                           };
                        });
                     }}
                     label={t("companyInfo.companyAddress")}
                     value={
                        values.address
                           ? [
                                values.address.addressLine1,
                                values.address.city,
                                values.address.state,
                                values.address.zipCode,
                             ]
                                .filter(Boolean)
                                .join(", ")
                           : ""
                     }
                     required
                     error={hasSubmitted && validation.errors.address}
                  />

                  {/* Phone Number (optional) */}
                  <PhoneInput
                     label={t("companyInfo.phoneNumber")}
                     value={values.phoneNumber}
                     setValue={(newValue) =>
                        setValues((prev) => ({
                           ...prev,
                           phoneNumber: newValue,
                        }))
                     }
                     required
                     setValid={setPhoneValid}
                     placeholder={t("companyInfo.phoneNumberPlaceholder")}
                     helperText={
                        hasSubmitted && values.phoneNumber.trim() && !phoneValid
                           ? t("companyInfo.phoneNumberInvalid")
                           : ""
                     }
                  />

                  {isOutbound && (
                     <CompanyInfoTwilioFields
                        businessIdType={values.businessIdType}
                        businessId={values.businessId}
                        twilioBusinessType={values.twilioBusinessType}
                        hasSubmitted={hasSubmitted}
                        businessIdError={validation.errors.businessId}
                        twilioBusinessTypeError={validation.errors.twilioBusinessType}
                        businessIdHelperText={businessIdHelperText}
                        onChange={(patch) => setValues((prev) => ({...prev, ...patch}))}
                     />
                  )}
               </div>
            </div>
         </div>

         {/* Footer Buttons */}
         <div
            className={clsx(
               "sticky bottom-0 flex items-center gap-4 px-8 py-4",
               "bg-panel_secondary/50 border-t border-200 backdrop-blur-md"
            )}
         >
            {onBack && (
               <Button
                  type="tertiary"
                  className="min-w-[120px]"
                  onClick={onBack}
                  disabled={isSaving}
               >
                  {t("wizard.back")}
               </Button>
            )}
            <Button
               type="primary"
               className="min-w-[120px] ml-auto"
               onClick={handleSave}
               disabled={isSaving}
            >
               {isSaving ? t("wizard.saving") : t("wizard.next")}
            </Button>
         </div>
      </div>
   );
}
